import { useGetDevice, useGetDocumentsTemplates, useGetUser } from "@hooks";
import { Document, DocumentVersion } from "@models";
import { useParams } from "react-router-dom";
import {
  DocViewerErrorBoundary,
  FileDocViewerErrorBoundary,
} from "src/components/ErrorBoundary";
import { TiptapEditor } from "src/components/TiptapEditor";
import { DocProgress } from "../../types";
import { DocumentViewerSkeleton } from "../Skeletons";
import NoDocAnimation from "./components/NoDocAnimation";
import { FileDocViewer } from "./components/fileDocViewer/FileDocViewer";

const DocViewer = ({
  documents,
  deviceId,
  docId,
  documentWithAllVersions,
  isLifecycleDeleted = false,
  selectedDocVersion,
  docProgress,
  isLoading,
}: {
  documents: Document[] | undefined;
  deviceId: string;
  docId: string | undefined;
  documentWithAllVersions: Document | null | undefined;
  isLifecycleDeleted?: boolean;
  selectedDocVersion: DocumentVersion | undefined;
  docProgress: DocProgress | undefined;
  isLoading: boolean;
}) => {
  const selectedDocFromList = docId
    ? documents?.find((doc) => doc.id === docId)
    : undefined;
  const selectedDocument = documentWithAllVersions ?? selectedDocFromList;
  const isDeleted =
    isLifecycleDeleted || Boolean(selectedDocument?.softDeletionEvent);
  const templateKey = selectedDocument?.templateKey ?? undefined;
  const { orgId = "" } = useParams();

  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
    enabled: Boolean(deviceId) && !isDeleted,
  });
  const { data: device } = useGetDevice({ orgId, deviceId });
  const { data: user } = useGetUser();

  if (
    isLoading ||
    !documents ||
    !user ||
    (!isDeleted && Boolean(deviceId) && (!device || !templatesConfig))
  ) {
    return <DocumentViewerSkeleton />;
  }

  if (!docId || !selectedDocument) return <NoDocAnimation type="NoDoc" />;

  if (!isDeleted && docProgress?.enabled === false) {
    return <NoDocAnimation type="LockedDoc" />;
  }

  if (selectedDocument.userUpload) {
    return (
      <FileDocViewerErrorBoundary
        deviceId={deviceId}
        templateKey={templateKey}
        orgId={orgId}
        userId={user?.id}
        isUserUpload={true}
      >
        <FileDocViewer
          deviceId={deviceId}
          docWithAllVersions={selectedDocument}
          selectedDocVersion={selectedDocVersion}
          key={docId}
        />
      </FileDocViewerErrorBoundary>
    );
  }

  return (
    <DocViewerErrorBoundary
      deviceId={deviceId}
      templateKey={templateKey}
      orgId={orgId}
      userId={user?.id}
    >
      <TiptapEditor
        document={selectedDocument}
        documentId={docId}
        isLifecycleDeleted={isLifecycleDeleted}
        selectedDocVersion={selectedDocVersion}
      />
    </DocViewerErrorBoundary>
  );
};

export default DocViewer;
